import clsx from 'clsx'
import _ from 'lodash'

import { Badge } from '@/shared/ui/Badge'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'
import { useResourceContext } from '@/shared/ui/Resource'
import { RewardBadge } from '@/shared/ui/RewardBadge'
import type { Document } from '@/types/document'

import { ResourceTypeBadge } from './ResourceTypeBadge'
import { getStatusBadge } from '../lib/getStatusBadge'

type ResourceCardProps = {
  document: Document
  onOpen: () => void
}

export const ResourceCard = ({ document, onOpen }: ResourceCardProps) => {
  const {
    locked: lockedByUnlock,
    hasFinished,
    hasRewards,
    pointsReward,
    coinsReward
  } = useResourceContext()
  // Mesma regra de `useLibraryView`: concluído vence bloqueado.
  const locked = lockedByUnlock && !hasFinished
  const status = getStatusBadge(hasFinished, locked)

  const thumbUrl = _.get(document.thumb, 'file.url')

  return (
    <Card
      onClick={locked ? undefined : onOpen}
      className={clsx(
        'cursor-pointer shadow-sm transition-shadow hover:shadow-md',
        locked && 'pointer-events-none opacity-60 grayscale'
      )}
    >
      <Card.Body className="!p-2.5">
        <div className="flex items-stretch gap-3">
          {/* Thumbnail */}
          <div className="flex h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-gray-200">
            {thumbUrl ? (
              <img
                src={thumbUrl}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ResourceTypeBadge kind={document.archive_kind} />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
            <div className="flex items-center gap-1.5">
              {status && (
                <Badge variant={status.variant} size="sm" showIcon>
                  {status.label}
                </Badge>
              )}
              <ResourceTypeBadge kind={document.archive_kind} />
            </div>

            <h3 className="m-0 line-clamp-2 text-sm font-bold leading-snug text-gray-900">
              {document.name}
            </h3>

            {hasRewards && !hasFinished && !locked && (
              <RewardBadge.Group>
                {pointsReward > 0 && (
                  <RewardBadge type="xp" value={`+${pointsReward}`} variant="inline" />
                )}
                {coinsReward > 0 && (
                  <RewardBadge type="coins" value={`+${coinsReward}`} variant="inline" />
                )}
              </RewardBadge.Group>
            )}
          </div>

          {/* CTA column */}
          <div className="flex shrink-0 flex-col items-end justify-end">
            {!locked && (
              <Button
                variant={hasFinished ? 'success-ghost' : 'primary'}
                size="sm"
                onClick={e => {
                  e.stopPropagation()
                  onOpen()
                }}
              >
                {hasFinished ? 'Revisitar' : 'Acessar'}
              </Button>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
